/**
 * ⌘K — jump to any project, walk or item in the workspace you are looking at.
 *
 * Renders `GET /api/search`, i.e. `searchConsole` over the caller's scope. The
 * palette itself decides nothing about what is findable: the ceiling is the
 * server's, and a hit that reaches this component is a hit the caller is
 * entitled to (see `server/queries/searchConsole.ts`).
 *
 * ## Four states, and none of them look alike
 *
 *  - **Nothing typed.** A hint, not a spinner and not an empty-result message.
 *    An open palette with an empty box is a normal state.
 *  - **Searching.** Says so.
 *  - **Nothing matched.** Names the query back — "Nothing matches «foo»" —
 *    because a blank panel cannot be told apart from a broken one.
 *  - **A failure.** Says it failed, with the status. This is the distinction
 *    the whole console is built on and the palette does not get an exemption.
 *
 * ## Why the query is debounced and the result is token-guarded
 *
 * Typing "riverside" is nine requests. Without a debounce the palette makes
 * all nine; without the token guard the SLOWEST of them wins, and the list
 * under the cursor ends up answering a prefix of what is in the box. Both are
 * cheap; the second is the one that produces a palette people distrust.
 */

import { useEffect, useRef, useState } from "react";
import { fetchSearch, type SearchHitRow, type SearchResponse } from "../api";
import { C, FONT_MONO, projectDot } from "../tokens";
import { KIND_ICON, KIND_LABEL, META } from "../components/chrome";

/** Long enough to swallow a burst of keystrokes, short enough that the list
 *  feels attached to the keyboard. */
const DEBOUNCE_MS = 160;

export interface CommandKProps {
  open: boolean;
  onClose: () => void;
  /** The console URL, forwarded verbatim so the palette can never answer for
   *  a different workspace than the page behind it. */
  search: string;
  onOpenItem: (itemId: string) => void;
  onOpenProject: (jobSiteId: string) => void;
  onOpenWalk: (visitId: string) => void;
}

function HitIcon({ hit }: { hit: SearchHitRow }) {
  if (hit.type === "site") {
    return (
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 8,
          background: C.faint,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <span
          style={{ width: 8, height: 8, borderRadius: 4, background: projectDot(hit.jobSiteId) }}
        />
      </span>
    );
  }

  const icon = hit.kind ? KIND_ICON[hit.kind] : null;
  return (
    <span
      style={{
        width: 26,
        height: 26,
        borderRadius: 8,
        background: C.faint,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke={C.sub}
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {icon ? (
          <>
            <path d={icon.d1} />
            <path d={icon.d2} />
          </>
        ) : (
          // A walk.
          <>
            <path d="M12 3a9 9 0 100 18 9 9 0 000-18Z" />
            <path d="M12 7v5l3 2" />
          </>
        )}
      </svg>
    </span>
  );
}

function Group({
  label,
  hits,
  onPick,
}: {
  label: string;
  hits: SearchHitRow[];
  onPick: (hit: SearchHitRow) => void;
}) {
  if (hits.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ ...META, fontSize: 9.77, color: C.sub2, padding: "6px 14px 4px", letterSpacing: "1px" }}>
        {label}
      </div>
      {hits.map((hit) => (
        <button
          key={`${hit.type}:${hit.id}`}
          data-testid="cmdk-hit"
          data-hit-type={hit.type}
          data-hit-id={hit.id}
          onClick={() => onPick(hit)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 11,
            padding: "9px 14px",
            textAlign: "left",
          }}
        >
          <HitIcon hit={hit} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: "block",
                fontSize: 15.52,
                fontWeight: 600,
                letterSpacing: "-0.2px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {hit.title}
            </span>
            <span
              style={{
                display: "block",
                fontFamily: FONT_MONO,
                fontSize: 10.93,
                color: C.sub2,
                marginTop: 3,
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {hit.kind ? `${KIND_LABEL[hit.kind]} · ${hit.meta}` : hit.meta}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

export function CommandK({
  open,
  onClose,
  search,
  onOpenItem,
  onOpenProject,
  onOpenWalk,
}: CommandKProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const token = useRef(0);

  // Reopening starts clean. A palette that showed the last search's results
  // under an empty box would be answering a question nobody asked.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults(null);
    setProblem(null);
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (!trimmed) {
      token.current += 1;
      setResults(null);
      setSearching(false);
      setProblem(null);
      return;
    }

    const mine = (token.current += 1);
    setSearching(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const next = await fetchSearch(search, trimmed);
          if (mine !== token.current) return;
          setResults(next);
          setProblem(null);
        } catch (error) {
          if (mine !== token.current) return;
          setResults(null);
          setProblem(
            (error as { status?: number; message?: string }).message ?? "Search failed.",
          );
        } finally {
          if (mine === token.current) setSearching(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [open, query, search]);

  if (!open) return null;

  const pick = (hit: SearchHitRow) => {
    onClose();
    if (hit.type === "item") onOpenItem(hit.id);
    else if (hit.type === "site") onOpenProject(hit.id);
    else onOpenWalk(hit.id);
  };

  const nothingMatched = !!results && results.total === 0;

  return (
    <div
      data-testid="cmdk"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: C.scrim,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "12vh 24px 24px",
        zIndex: 60,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          background: C.surface,
          borderRadius: 18,
          border: `1px solid ${C.line}`,
          overflow: "hidden",
          boxShadow: `0 30px 70px -20px ${C.scrim}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            padding: "14px 16px",
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
            <circle cx="8" cy="8" r="5.4" stroke={C.sub} strokeWidth="1.6" />
            <path d="M12 12l4 4" stroke={C.sub} strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            data-testid="cmdk-input"
            value={query}
            // `onInput`, not `onChange`. They are the same event for a text
            // input in React, and this one is the one that actually fires
            // under the console's own test DOM (React 19 + happy-dom never
            // synthesises the change event for a text input, so an
            // `onChange` handler here would be untestable — and an
            // untestable composer is how a typed address silently stops
            // reaching the send button).
            onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") onClose();
            }}
            placeholder="Search projects, walks and items…"
            style={{
              flex: 1,
              border: 0,
              outline: "none",
              background: "transparent",
              fontSize: 17.25,
              color: C.ink,
            }}
          />
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: 11.5,
              fontWeight: 700,
              color: C.sub,
              background: C.faint,
              borderRadius: 5,
              padding: "2px 6px",
            }}
          >
            ESC
          </span>
        </div>

        <div className="sb" style={{ maxHeight: "50vh", overflow: "auto", padding: "8px 0 12px" }}>
          {!query.trim() && (
            <div
              data-testid="cmdk-hint"
              style={{ padding: "18px 16px", fontSize: 14.37, color: C.sub2, lineHeight: 1.5 }}
            >
              Type to jump to a project, a walk, or an item — anywhere in the workspace you are
              looking at.
            </div>
          )}

          {problem && (
            <div
              data-testid="cmdk-error"
              style={{ padding: "18px 16px", fontSize: 14.37, color: C.red, lineHeight: 1.5 }}
            >
              {problem}
            </div>
          )}

          {searching && !results && (
            <div style={{ ...META, fontSize: 10.35, color: C.sub2, padding: "18px 16px" }}>
              Searching
            </div>
          )}

          {nothingMatched && !problem && (
            <div
              data-testid="cmdk-empty"
              style={{ padding: "18px 16px", fontSize: 14.37, color: C.sub, lineHeight: 1.5 }}
            >
              {`Nothing matches “${results!.query}” in this workspace.`}
            </div>
          )}

          {results && results.total > 0 && (
            <>
              <Group label="Projects" hits={results.sites} onPick={pick} />
              <Group label="Walks" hits={results.visits} onPick={pick} />
              <Group label="Items" hits={results.items} onPick={pick} />
              {results.truncated && (
                <div
                  data-testid="cmdk-truncated"
                  style={{ padding: "6px 16px 0", fontSize: 13.23, color: C.sub2 }}
                >
                  {`Showing the closest matches of ${results.total}. Keep typing to narrow it.`}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
